<template>
    <validation-observer ref="form" v-slot="{ meta }">
        <modal
            id="select-guest-modal"
            title="Select/Create Guest"
            scrollable
            :ok-disabled="!meta.valid"
            @shown="onModalShown"
            no-fade
            :static="true"
        >
            <busy-modal :show="busy"></busy-modal>
            <b-form v-if="values">
                <b-row>
                    <b-col md="6">
                        <validation-provider name="first_name" v-slot="{ errors }" rules="required">
                            <form-input
                                floating-label="First Name"
                                v-model="values.first_name"
                                :left-icon="['fas', 'user']"
                                @input="searchGuests()"
                                :error="errors[0]"
                                :readonly="isSelected"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="last_name" v-slot="{ errors }" rules="required">
                            <form-input
                                floating-label="Last Name"
                                v-model="values.last_name"
                                :left-icon="['fas', 'user']"
                                @input="searchGuests()"
                                :error="errors[0]"
                                :readonly="isSelected"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <validation-provider
                            v-if="!autoGenerate"
                            name="email"
                            v-slot="{ errors }"
                            rules="required|email"
                        >
                            <form-input
                                floating-label="Email"
                                type="email"
                                v-model="values.email"
                                :left-icon="['fas', 'envelope']"
                                @input="searchGuests()"
                                :error="errors[0]"
                                :rightButton="{
                                    title: 'Auto-generate',
                                    icon: ['fas', 'user-gear'],
                                    disabled: isEdit(),
                                    variant: 'outline-primary',
                                }"
                                @handle-right-button-click="toggleAutoGenerate()"
                            ></form-input>
                        </validation-provider>
                        <form-input
                            v-if="autoGenerate"
                            floating-label="Email"
                            value="Email will be auto-generated."
                            :left-icon="['fas', 'envelope']"
                            :disabled="true"
                            :rightButton="{
                                title: 'Auto-generate',
                                icon: ['fas', 'user-gear'],
                                disabled: isEdit(),
                                variant: 'primary',
                            }"
                            @handle-right-button-click="toggleAutoGenerate()"
                        ></form-input>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <hr />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <div v-if="guestList.length">
                            <div
                                class="guest-item p-2"
                                :key="guest.id"
                                v-for="guest in guestList"
                                @click="selectGuestFromList(guest)"
                            >
                                <b-row>
                                    <b-col md="1" v-if="hasStatus">
                                        <span class="d-flex align-items-center">
                                            <img
                                                v-if="guest.is_vip"
                                                class="guest-status-image"
                                                title="VIP"
                                                src="/static/images/icons/reservation/vip_bg_24.svg"
                                            />
                                            <img
                                                v-if="guest.is_blocklist"
                                                class="guest-status-image"
                                                title="Blocklist"
                                                src="/static/images/icons/reservation/blocklist_bg_24.svg"
                                            />
                                        </span>
                                    </b-col>
                                    <b-col md="11">
                                        <span>{{ guest.first_name }} {{ guest.last_name }} - {{ guest.email }}</span>
                                    </b-col>
                                </b-row>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </b-form>

            <template #modal-footer="{ ok, cancel }">
                <div class="w-100">
                    <b-button variant="outline-primary" class="float-left" @click="clear()">
                        Clear
                    </b-button>
                    <b-button
                        variant="primary"
                        class="float-right accept-button"
                        :disabled="!meta.valid"
                        @click="setGuest()"
                    >
                        Accept
                    </b-button>
                    <b-button variant="secondary" class="float-right" @click="cancel()">
                        Cancel
                    </b-button>
                </div>
            </template>
        </modal>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';

    export default {
        name: 'SelectGuestModal',
        components: {
            BusyModal,
            Modal,
            FormInput,
        },
        props: {
            show: {
                type: Boolean,
                default: false,
            },
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                autoGenerate: false,
                busy: false,
                values: null,
                hasStatus: false,
                reservationDetails: {},
                isSelected: false,
            };
        },
        methods: {
            cancel() {
                this.$store.dispatch('reservation/resetGuestList');
            },
            clear() {
                this.values.id = null;
                this.values.first_name = '';
                this.values.last_name = '';
                this.values.email = '';
                this.searchGuests();
                this.isSelected = false;
            },
            getInitialValues() {
                return {
                    id: this.initialValues.user_id || null,
                    email: this.initialValues.email || '',
                    first_name: this.initialValues.first_name || '',
                    last_name: this.initialValues.last_name || '',
                };
            },
            async searchGuests() {
                try {
                    await this.$store.dispatch('reservation/fetchGuestList', {
                        email: this.values.email,
                        first_name: this.values.first_name,
                        last_name: this.values.last_name,
                    });

                    this.isSelected = false;
                    
                    let found = false;
                    for (var i = 0; i < this.guestList.length; i++) {
                        if (this.guestList[i].is_vip || this.guestList[i].is_blocklist) {
                            found = true;
                            break;
                        }
                    }
                    if (found) {
                        this.hasStatus = true;
                    } else {
                        this.hasStatus = false;
                    }
                    if (this.guestList.length > 0 && this.values.email.toLowerCase() == this.guestList[0].email.toLowerCase()) {
                        this.selectGuestFromList(this.guestList[0]);
                    }
                } catch (err) {
                    console.log(err);
                    this.errorToast({ text: 'Could not load guest data.' });
                }
            },
            async setGuest() {
                let values = this.values;
                values.autoGenerate = this.autoGenerate;
                this.$emit('set-reservation-guest', values);
                this.$store.dispatch('reservation/resetGuestList');
                this.$bvModal.hide('select-guest-modal');
            },
            async selectGuestFromList(guest) {
                this.autoGenerate = false;
                this.values.id = guest.id;
                this.values.first_name = guest.first_name;
                this.values.last_name = guest.last_name;
                this.values.email = guest.email;
                this.isSelected = true;
                //this.searchGuests();
            },
            onModalShown() {
                (async () => {
                    this.values = this.getInitialValues();
                    this.searchGuests();
                })();
            },
            toggleAutoGenerate() {
                if (!this.isEdit()) {
                    this.autoGenerate = !this.autoGenerate;
                    if(!this.autoGenerate) {
                        this.$refs.form.setFieldValue('email', this.values.email);
                    }
                }
            },
            isEdit() {
                return this.reservationDetails.id != null;
            },
        },
        computed: {
            ...mapGetters('reservation', ['guestList']),
        },
        mounted() {
            (async () => {
                this.values = this.getInitialValues();
            })();
        },
        watch: {
            values: {
                handler(values) {
                    this.$refs.form.setValues(values)
                },
                deep: true
            }
        }
    };
</script>

<style scoped lang="scss">
    .guest-item {
        cursor: pointer;
        &:hover {
            background: #0d47a1;
            color: #fff;
        }
    }
    .guest-status-image {
        width: 21px;
        height: 21px;
    }
    .accept-button {
        margin-left: 5px;
    }
</style>
